import { z } from 'zod';

import { IsoDateTimeSchema } from '../common/datetime';
import { SessionId } from '../common/ids';

import { AdminEmailSchema, AdminUserSchema } from './admins';
import { CompanySchema, CurrentCompanyResponseSchema } from './companies';

// ---------------------------------------------------------------------------
// Contraseñas
// ---------------------------------------------------------------------------

/**
 * Contraseña tal como llega en el body. La política (10–128 caracteres, lista de filtradas/HIBP)
 * la evalúa la API para responder `422 PASSWORD_WEAK` / `422 PASSWORD_COMPROMISED`
 * en lugar de `400 VALIDATION_ERROR` (contracts/errors.md).
 */
export const PasswordInputSchema = z.string().min(1).max(1024);

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;

export const PasswordTokenPurpose = z.enum(['SETUP', 'RESET']);
export type PasswordTokenPurpose = z.infer<typeof PasswordTokenPurpose>;

// ---------------------------------------------------------------------------
// Login / logout
// ---------------------------------------------------------------------------

/** `POST /auth/login` */
export const LoginBodySchema = z.object({
  email: AdminEmailSchema,
  password: PasswordInputSchema,
});
export type LoginBody = z.infer<typeof LoginBodySchema>;

/**
 * `200 { admin, company: Company|null, support_mode: false }` + Set-Cookie
 *
 * `company` sigue la misma regla que `GET /me` y `GET /company`: sin `settings` cuando el actor
 * no tiene `company.settings.write` (US2, T055; ver "Dudas y desvíos" de implementation-notes).
 */
export const LoginResponseSchema = z.object({
  admin: AdminUserSchema,
  company: CurrentCompanyResponseSchema.nullable(),
  support_mode: z.literal(false),
});
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

// ---------------------------------------------------------------------------
// Recuperación / configuración de contraseña
// ---------------------------------------------------------------------------

/** `POST /auth/password/forgot` → `202 {}` siempre. */
export const ForgotBodySchema = z.object({
  email: AdminEmailSchema,
});
export type ForgotBody = z.infer<typeof ForgotBodySchema>;

/** Parámetro de ruta `{token}` de `GET /auth/password/tokens/{token}`. */
export const PasswordTokenParamsSchema = z.object({
  token: z.string().min(1).max(512),
});
export type PasswordTokenParams = z.infer<typeof PasswordTokenParamsSchema>;

/** `200 { purpose, email_hint: 'm***@overall.pe', expires_at }` */
export const PasswordTokenInfoSchema = z.object({
  purpose: PasswordTokenPurpose,
  email_hint: z.string(),
  expires_at: IsoDateTimeSchema,
});
export type PasswordTokenInfo = z.infer<typeof PasswordTokenInfoSchema>;

/** `POST /auth/password/complete` */
export const CompletePasswordBodySchema = z.object({
  token: z.string().min(1).max(512),
  password: PasswordInputSchema,
});
export type CompletePasswordBody = z.infer<typeof CompletePasswordBodySchema>;

/** `200 { admin, company }` + Set-Cookie (inicia sesión). */
export const CompletePasswordResponseSchema = z.object({
  admin: AdminUserSchema,
  company: CompanySchema.nullable(),
});
export type CompletePasswordResponse = z.infer<typeof CompletePasswordResponseSchema>;

/** `PATCH /me/password` */
export const ChangePasswordBodySchema = z.object({
  current_password: PasswordInputSchema,
  new_password: PasswordInputSchema,
});
export type ChangePasswordBody = z.infer<typeof ChangePasswordBodySchema>;

// ---------------------------------------------------------------------------
// Sesiones
// ---------------------------------------------------------------------------

export const SessionSchema = z.object({
  id: SessionId,
  created_at: IsoDateTimeSchema,
  last_seen_at: IsoDateTimeSchema,
  ip: z.string().nullable(),
  user_agent: z.string().nullable(),
  current: z.boolean(),
  support_mode: z.boolean(),
});
export type Session = z.infer<typeof SessionSchema>;

/** `GET /me/sessions` → `200 { items: Session[] }` */
export const SessionListResponseSchema = z.object({
  items: z.array(SessionSchema),
});
export type SessionListResponse = z.infer<typeof SessionListResponseSchema>;

/** Parámetro de ruta `{id}` de `DELETE /me/sessions/{id}`. */
export const SessionIdParamsSchema = z.object({ id: SessionId });
export type SessionIdParams = z.infer<typeof SessionIdParamsSchema>;

// ---------------------------------------------------------------------------
// Perfil propio (/me)
// ---------------------------------------------------------------------------

/**
 * Permisos efectivos calculados por `authorize()` (implementation-notes §4).
 * La UI oculta acciones según esta lista; la API siempre revalida.
 */
export const PERMISSIONS = [
  // --- 001 ---
  'companies.manage',
  'company.read',
  'company.settings.write',
  'sites.read',
  'sites.write',
  'admins.read',
  'admins.write',
  'audit.read',
  'me.manage',
  // --- 002 (data-model.md "Autorización") ---
  'workers.read',
  'workers.write',
  'workers.invite',
  'workers.deactivate',
  'devices.revoke',
  'rebind.resolve',
  // --- 003 (data-model.md "Autorización"; research R-13) ---
  'terminals.read',
  'terminals.write',
  'terminals.disable',
  // --- 005 (research R-5; contracts/admin-audit-settings.md §1) ---
  'attendance.read',
  'attendance.location.read',
  'attendance.export',
  'attendance.write',
  // --- 008 (research R-22; contracts/errors-permissions-audit.md §3) ---
  'schedules.read',
  'schedules.write',
  'schedules.configure',
  'timesheet.export',
  // --- 009 (research R-24; contracts/errors-permissions-audit.md §3): solo TENANT_FULL ---
  'integration.manage',
] as const;

export const PermissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof PermissionSchema>;

/**
 * Avisos que la UI debe mostrar:
 * - `NO_SITES_IN_SCOPE` (001): SITE_MANAGER sin sedes activas en su alcance.
 * - `PENDING_REBIND_REQUESTS` (002): hay solicitudes de re-vinculación pendientes en el alcance.
 */
export const MeWarningSchema = z.enum(['NO_SITES_IN_SCOPE', 'PENDING_REBIND_REQUESTS']);
export type MeWarning = z.infer<typeof MeWarningSchema>;

/** Contadores para badges del menú (contracts/admin-workers.md · `GET /me`). */
export const MeCountsSchema = z.object({
  /** Solicitudes de re-vinculación `PENDING` en el alcance del actor; 0 si no aplica. */
  pending_rebind_requests: z.number().int().nonnegative(),
  /**
   * 007 · solicitudes de regularización `PENDING` en el alcance del actor si tiene
   * `attendance.write`; si no, 0.
   */
  pending_regularization_requests: z.number().int().nonnegative(),
  /**
   * 008 · justificaciones `PENDING` en el alcance del actor (sede del día) si tiene
   * `attendance.write`; si no, 0.
   */
  pending_justification_requests: z.number().int().nonnegative(),
  /**
   * 009 · revisiones de integración `OPEN` de la empresa si el actor tiene `integration.manage`; si
   * no, 0.
   */
  pending_integration_reviews: z.number().int().nonnegative(),
});
export type MeCounts = z.infer<typeof MeCountsSchema>;

/**
 * 009 · conexión de la empresa con un sistema externo, para la leyenda "Gestionado por <sistema>".
 * La ve todo rol con empresa (sin permiso) si la conexión existe (`ACTIVE` o `DISABLED`).
 */
export const MeIntegrationSchema = z.object({
  system_name: z.string().min(1).max(80),
});
export type MeIntegration = z.infer<typeof MeIntegrationSchema>;

/** `GET /me` */
export const MeResponseSchema = z.object({
  admin: AdminUserSchema,
  /** Sin `settings` cuando el actor no tiene `company.settings.write` (misma regla que `GET /company`). */
  company: CurrentCompanyResponseSchema.nullable(),
  support_mode: z.boolean(),
  permissions: z.array(PermissionSchema),
  warnings: z.array(MeWarningSchema),
  counts: MeCountsSchema,
  /** 009 · `null` sin conexión o sin empresa. */
  integration: MeIntegrationSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;
